import React, { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import Envelope from '@pkg/assets/images/Envelope.png';
import PartyPopper from '@pkg/assets/images/Party_Popper.png';
import { DepButton, DepButtonDisplay, DepExternalLinkButton, DepInput } from '@pkg/components/deprecated';
import { useAppDispatch } from '@pkg/hooks';

import { type ApiResult, type MagicLinkResponseValue } from '@app/client';
import { validEmail } from '@app/helpers';
import { passwordResetEmail } from '@app/store/actions/auth';
import { addNotification } from '@app/store/actions/notification';

import './_ForgotPasswordForm.scss';

enum Steps {
    Email,
    Confirmation,
}

const YatForgotPasswordForm = () => {
    const { t } = useTranslation(['account', 'translation', 'common']);
    const { emailParam } = useParams();
    const [email, setEmail] = useState(emailParam || '');
    const [step, setStep] = useState<Steps>(Steps.Email);
    const [isSending, setIsSending] = useState(false);
    const dispatch = useAppDispatch();

    const handleSubmit = useCallback(
        (e) => {
            e.preventDefault();
            if (!email) return;

            if (validEmail(email)) {
                const dispatchedPromise = dispatch(passwordResetEmail(email.trim()) as unknown) as Promise<
                    ApiResult<MagicLinkResponseValue>
                >;
                setIsSending(true);
                dispatchedPromise
                    .then((r) => {
                        if (r.ok) {
                            setStep(Steps.Confirmation);
                        }
                    })
                    .catch((e) => {
                        dispatch(
                            addNotification({
                                message: e.error,
                                severity: 'error',
                            }),
                        );
                    })
                    .finally(() => {
                        setIsSending(false);
                    });
                return dispatchedPromise;
            }
            dispatch(
                addNotification({
                    message: t('valid_email_request'),
                    title: t('common:error_invalid_email'),
                    severity: 'alert',
                }),
            );
        },
        [email, dispatch, t],
    );

    const handleChange = (e) => {
        const { value } = e.target;
        setEmail(value);
    };

    if (step === Steps.Confirmation) {
        return (
            <div className="forgot-password-form">
                <img className="user-account-page__img" src={PartyPopper} alt={t('translation:yat_smileys')} />
                <h1 className="v2-text-heading-2">{t('account:forgot_password_sent_title')}</h1>
                <p className="user-account-page__copy user-account-page__copy--with-input v2-text-fluid-md">
                    {t('account:forgot_password_sent_copy')}
                </p>
                <form className={'forgot-password-form__form'} onSubmit={handleSubmit}>
                    <div className="user-account-page__btn">
                        <DepButton
                            display={DepButtonDisplay.minWidth}
                            variant="large"
                            type="submit"
                            loading={isSending}
                        >
                            {t('account:forgot_password_resend')}
                        </DepButton>
                        <DepExternalLinkButton href="https://support.y.at" variant="invisible">
                            {t('account:forgot_password_support')}
                        </DepExternalLinkButton>
                    </div>
                </form>
            </div>
        );
    }

    return (
        <div className="forgot-password-form">
            <img className="user-account-page__img" src={Envelope} alt={t('translation:yat_smileys')} />
            <h1 className="v2-text-heading-2">{t('account:forgot_password_title')}</h1>
            <p className="user-account-page__copy user-account-page__copy--with-input v2-text-fluid-md">
                {t('account:forgot_password_copy')}
            </p>
            <form className={'forgot-password-form__form'} onSubmit={handleSubmit}>
                <DepInput
                    isInvalid={false}
                    classes="user-account-page__input"
                    autoComplete="email"
                    placeholder={t('common:email')}
                    type="email"
                    value={email}
                    onChange={handleChange}
                    required
                />
                <div className="user-account-page__btn">
                    <DepButton display={DepButtonDisplay.minWidth} variant="large" type="submit" loading={isSending}>
                        {t('account:forgot_password_send_reset')}
                    </DepButton>
                </div>
            </form>
        </div>
    );
};

export default YatForgotPasswordForm;
